import React from 'react'
import { LayoutBand, Paragraph, Separator, useContainerWidth, H2, Image } from '@fs/zion-ui'
import { PillButton } from './buttons'

interface HeroImageProps {
  image?: string
  minHeight?: number
  heading?: string | React.ReactNode
  description?: string
  buttonLabel?: string
  to?: string
  maxHeaderWidth?: number
  align?: 'left' | 'center' | 'right'
  alt?: string
}

/**
 * Page-width hero image with optional heading, descriptive text, and button.
 * Props are documented on the `HeroImageProps` interface above.
 */
export default function HeroImage({
  image = '',
  minHeight = 700,
  heading = '', // can take a custom JSX object, tho React doesn't like it
  description = '',
  buttonLabel = '',
  to = '/',
  maxHeaderWidth = 500,
  align = 'center',
  alt = '',
}: HeroImageProps): JSX.Element {
  const atWidth = useContainerWidth()

  return (
    <LayoutBand data-testid="imageContent" alignY="bottom">
      {image && (
        <>
          <Image src={image} alt={alt} height={atWidth({ default: 350, xs: 350, md: 500, lg: minHeight })} />
          <Separator size="xs" />
        </>
      )}
      <LayoutBand
        data-testid="textContent"
        style={{ textAlign: align, maxWidth: maxHeaderWidth, marginInline: 'auto' }}
      >
        {heading && <H2 supplementary>{heading}</H2>}
        <Separator size="xs" />
        {description && <Paragraph size="sm">{description}</Paragraph>}
        <Separator size="xxs" />
        {buttonLabel && (
          <PillButton to={to} linkName="Hero: Button">
            {buttonLabel}
          </PillButton>
        )}
      </LayoutBand>
    </LayoutBand>
  )
}
